// Helpers for the public club profile page. Kept separate so the club-type wording,
// image optimisation and colour math are testable and reused in one place.

// ── Club-type flexibility — SINGLE source of truth for all vertical wording ──────
// The vertical belongs to the CLUB (Club.clubType), not the login. `vocab(clubType)` is used
// everywhere (admin, trainer, public) via the useClubVocab() hook, so a gym reads
// "Members / Sessions / Trainers" while a taekwondo club reads "Students / Classes / Coaches".
//
// Canonical Stage-1 types: 'taekwondo' | 'gym' | 'yoga' | 'general'. Legacy values map by family
// so existing clubs render byte-identically to today.
const GYM_TYPES  = new Set(['gym', 'fitness', 'general']);
const YOGA_TYPES = new Set(['yoga']);
// Everything else (taekwondo, martial_arts, boxing, mma, dance, school, other, custom, unset)
// → the "Student" set.

const TYPE_LABELS = {
  taekwondo:    'Taekwondo',
  gym:          'Gym',
  yoga:         'Yoga Studio',
  general:      'Club',
  martial_arts: 'Martial Arts',
  boxing:       'Boxing',
  mma:          'MMA',
  dance:        'Dance Studio',
  fitness:      'Fitness',
  school:       'Academy',
};

export function clubTypeLabel(type) {
  return TYPE_LABELS[type] || ''; // 'other' / 'custom' → no label (chip hidden)
}

// ── Capability: belts / grading (Stage 2 gating) ────────────────────────────────
// The belt world (belt exams, ranks, grading) is shown ONLY for martial-arts-style clubs.
// Gyms, yoga studios and general clubs hide it entirely (reversible — no deletion). One helper,
// used by nav, route guards, list columns, dashboards, launch readiness and the public page.
// Unknown/unset types default to TRUE so existing clubs stay byte-identical.
const NO_BELT_TYPES = new Set(['gym', 'yoga', 'fitness', 'general']);
export function hasBelts(clubType) {
  return !NO_BELT_TYPES.has(clubType);
}

// ── Capability: classes / sessions / attendance / training journal (Stage 2 gating) ──
// Gyms in Nepal are open-floor: members + payments + expiry are the product; scheduled classes,
// QR sessions, attendance and the training journal are noise. Yoga studios and general clubs DO
// run classes, so this gates on the canonical 'gym' type ONLY — NOT the whole gym vocab family
// (legacy 'fitness'/'general' clubs keep classes, staying byte-identical). One helper, used by
// nav, route guards, dashboards and launch readiness. Unknown/unset → true. Reversible —
// switching the type back restores everything with zero data loss.
const NO_CLASS_TYPES = new Set(['gym']);
export function hasClasses(clubType) {
  return !NO_CLASS_TYPES.has(clubType);
}

export function vocab(type) {
  const gym  = GYM_TYPES.has(type);
  const yoga = YOGA_TYPES.has(type);

  // Role/session words. `trainer(s)` is the functional role word used in the admin/trainer
  // portals; `coachPlural` is the public-page marketing word (kept for backward-compat).
  const member  = gym || yoga ? 'Member'  : 'Student';
  const members = gym || yoga ? 'Members' : 'Students';
  const trainer  = yoga ? 'Instructor'  : 'Trainer';
  const trainers = yoga ? 'Instructors' : 'Trainers';
  const session  = gym ? 'Session'  : 'Class';
  const sessions = gym ? 'Sessions' : 'Classes';

  return {
    member, members, trainer, trainers, session, sessions,
    // Public-page keys (unchanged behaviour):
    memberPlural:  members,
    sessionPlural: sessions,
    coachPlural:   gym ? 'Trainers' : yoga ? 'Instructors' : 'Coaches',
    joinCta:       gym || yoga ? 'Start training' : 'Join a class',
  };
}

// ── Cloudinary transforms ───────────────────────────────────────────────────────
// Inject f_auto,q_auto + sizing into a Cloudinary delivery URL so we never ship a raw
// multi-MB upload. Non-Cloudinary URLs (base64 data URLs, legacy links) pass through.
export function cldImage(url, { w, h, crop = 'fill', gravity } = {}) {
  if (!url || typeof url !== 'string') return url;
  const marker = '/upload/';
  const i = url.indexOf(marker);
  if (i === -1) return url; // not a Cloudinary URL — return as-is
  const parts = ['f_auto', 'q_auto'];
  if (w) parts.push(`w_${w}`);
  if (h) parts.push(`h_${h}`);
  if (w || h) parts.push(`c_${crop}`);
  if (gravity) parts.push(`g_${gravity}`);
  return `${url.slice(0, i + marker.length)}${parts.join(',')}/${url.slice(i + marker.length)}`;
}

// ── Colour math ─────────────────────────────────────────────────────────────────
export function hexToRgb(hex) {
  if (!hex || typeof hex !== 'string' || hex[0] !== '#') return { r: 226, g: 75, b: 74 };
  const s = hex.length === 4
    ? `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}`
    : hex;
  return {
    r: parseInt(s.slice(1, 3), 16),
    g: parseInt(s.slice(3, 5), 16),
    b: parseInt(s.slice(5, 7), 16),
  };
}

export function rgbStr(hex) {
  const { r, g, b } = hexToRgb(hex);
  return `${r},${g},${b}`;
}

const toHex = (n) => Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, '0');

function relLuminance({ r, g, b }) {
  const lin = (c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}

function rgbToHsl({ r, g, b }) {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0;
  const l = (max + min) / 2;
  const d = max - min;
  if (d !== 0) {
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
    else if (max === g) h = ((b - r) / d + 2) / 6;
    else h = ((r - g) / d + 4) / 6;
  }
  return { h, s, l };
}

function hslToRgb({ h, s, l }) {
  if (s === 0) { const v = l * 255; return { r: v, g: v, b: v }; }
  const hue = (p, q, t) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  return { r: hue(p, q, h + 1 / 3) * 255, g: hue(p, q, h) * 255, b: hue(p, q, h - 1 / 3) * 255 };
}

// A brand-colour variant that reads as TEXT on the near-black (#0a0908) background — used
// ONLY where the brand is text/thin accents, never for filled buttons (those keep the real
// brand with white text). A dark brand is brightened by raising HSL *lightness* while KEEPING
// its hue and saturation, so a deep red becomes a richer, vivid red — never a washed-out pink
// (which is what mixing toward white produced). Bright brands pass through untouched.
export function readableBrand(hex) {
  const rgb = hexToRgb(hex);
  if (relLuminance(rgb) >= 0.14) return hex; // already legible for accents
  const hsl = rgbToHsl(rgb);
  hsl.s = Math.max(hsl.s, 0.62); // stay saturated → rich, not pastel
  let l = hsl.l;
  while (l < 0.72) {
    l += 0.05;
    const out = hslToRgb({ ...hsl, l });
    if (relLuminance(out) >= 0.18) return `#${toHex(out.r)}${toHex(out.g)}${toHex(out.b)}`;
  }
  const out = hslToRgb({ ...hsl, l: 0.72 });
  return `#${toHex(out.r)}${toHex(out.g)}${toHex(out.b)}`;
}

// Brand mixed toward black — used for the no-cover hero fallback gradient.
export function brandDark(hex, amount = 0.72) {
  const { r, g, b } = hexToRgb(hex);
  return `#${toHex(r * (1 - amount))}${toHex(g * (1 - amount))}${toHex(b * (1 - amount))}`;
}

// ── GPS ──────────────────────────────────────────────────────────────────────────
// The Settings.gps field DEFAULTS to central Kathmandu (27.7172, 85.3240) — shared with
// check-in geofencing, so the default can't be removed there. On the public map that default
// means "never set", not "located in Kathmandu": a Bharatpur club would otherwise show a
// Kathmandu pin. Treat coords at (or ~50m within) that default as unset so the page falls back
// to an address-only card instead of a wrong map.
const DEFAULT_GPS = { lat: 27.7172, lng: 85.324 };
export function isRealGps(gps) {
  if (!gps || typeof gps.lat !== 'number' || typeof gps.lng !== 'number') return false;
  const near = Math.abs(gps.lat - DEFAULT_GPS.lat) < 0.0006 && Math.abs(gps.lng - DEFAULT_GPS.lng) < 0.0006;
  return !near;
}

// ── Asia/Kathmandu date helpers (UTC+5:45, no DST) ──────────────────────────────
const KTM_OFFSET_MS = (5 * 60 + 45) * 60 * 1000;
const DAY_NAMES = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

export function ktmTodayName() {
  const d = new Date(Date.now() + KTM_OFFSET_MS);
  return DAY_NAMES[d.getUTCDay()];
}

// Minutes since KTM midnight, for the open/closed check.
export function ktmMinutesNow() {
  const d = new Date(Date.now() + KTM_OFFSET_MS);
  return d.getUTCHours() * 60 + d.getUTCMinutes();
}
